<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';

const store = useStore();

// State
const clientId = ref('');
const clientSecret = ref('');
const showConfigForm = ref(false);

// Computed
const isConfigured = computed(() => store.getters['googleSheets/isConfigured']);
const isConnected = computed(() => store.getters['googleSheets/isConnected']);
const connectionEmail = computed(() => store.getters['googleSheets/getConnectionEmail']);
const uiFlags = computed(() => store.getters['googleSheets/getUIFlags']);

const isConfigFormValid = computed(() => {
  return clientId.value.trim() && clientSecret.value.trim();
});

// Methods
const checkConnection = async () => {
  await store.dispatch('googleSheets/checkConnectionStatus');
};

const saveOAuthConfig = async () => {
  try {
    const result = await store.dispatch('googleSheets/saveConfig', {
      clientId: clientId.value.trim(),
      clientSecret: clientSecret.value.trim(),
    });
    if (result.success) {
      showConfigForm.value = false;
      clientId.value = '';
      clientSecret.value = '';
      useAlert('Configuração do Google Sheets salva com sucesso');
    }
  } catch (error) {
    useAlert('Erro ao salvar configuração');
  }
};

const toggleConfigForm = () => {
  showConfigForm.value = !showConfigForm.value;
};

const connectGoogle = async () => {
  await store.dispatch('googleSheets/initiateOAuth');
};

const disconnectGoogle = async () => {
  await store.dispatch('googleSheets/disconnect');
  useAlert('Conta Google desconectada');
};

onMounted(() => {
  checkConnection();
});
</script>

<template>
  <SectionLayout
    title="Google Sheets"
    description="Configure a integração com Google Sheets para importar contatos de planilhas nas campanhas de WhatsApp."
  >
    <!-- Not Configured State -->
    <div v-if="!isConfigured" class="space-y-4">
      <div class="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
        <p class="text-sm text-amber-700 dark:text-amber-300 mb-4">
          Para usar o Google Sheets, configure as credenciais OAuth do Google Cloud.
          <a
            href="https://console.cloud.google.com/apis/credentials"
            target="_blank"
            class="underline font-medium"
          >
            Obter credenciais
          </a>
        </p>

        <div class="space-y-4">
          <WithLabel label="Client ID">
            <NextInput
              v-model="clientId"
              type="text"
              class="w-full"
              placeholder="Seu Google OAuth Client ID"
            />
          </WithLabel>

          <WithLabel label="Client Secret">
            <NextInput
              v-model="clientSecret"
              type="password"
              class="w-full"
              placeholder="Seu Google OAuth Client Secret"
            />
          </WithLabel>

          <div class="flex justify-end">
            <NextButton
              blue
              :disabled="!isConfigFormValid"
              :is-loading="uiFlags.isSavingConfig"
              @click="saveOAuthConfig"
            >
              Salvar Configuração
            </NextButton>
          </div>
        </div>
      </div>
    </div>

    <!-- Configured State -->
    <div v-else class="space-y-4">
      <div class="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
        <div>
          <h4 class="text-sm font-medium text-slate-700 dark:text-slate-200">
            Conexão Google
          </h4>
          <p v-if="isConnected" class="text-sm text-green-600 dark:text-green-400 mt-1">
            ✓ Conectado como: {{ connectionEmail }}
          </p>
          <p v-else class="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Não conectado - clique para autorizar
          </p>
        </div>
        <div class="flex gap-2">
          <NextButton
            v-if="!isConnected"
            blue
            :is-loading="uiFlags.isConnecting"
            @click="connectGoogle"
          >
            Conectar com Google
          </NextButton>
          <NextButton
            v-else
            slate
            :is-loading="uiFlags.isDisconnecting"
            @click="disconnectGoogle"
          >
            Desconectar
          </NextButton>
        </div>
      </div>

      <!-- Reconfigure Option -->
      <div>
        <button
          type="button"
          class="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 underline"
          @click="toggleConfigForm"
        >
          {{ showConfigForm ? 'Cancelar' : 'Reconfigurar credenciais OAuth' }}
        </button>
      </div>

      <!-- Reconfigure Form -->
      <div v-if="showConfigForm" class="p-4 rounded-lg border border-slate-200 dark:border-slate-700 space-y-4">
        <WithLabel label="Client ID">
          <NextInput
            v-model="clientId"
            type="text"
            class="w-full"
            placeholder="Novo Client ID"
          />
        </WithLabel>

        <WithLabel label="Client Secret">
          <NextInput
            v-model="clientSecret"
            type="password"
            class="w-full"
            placeholder="Novo Client Secret"
          />
        </WithLabel>

        <div class="flex justify-end gap-2">
          <NextButton slate @click="toggleConfigForm">
            Cancelar
          </NextButton>
          <NextButton
            blue
            :disabled="!isConfigFormValid"
            :is-loading="uiFlags.isSavingConfig"
            @click="saveOAuthConfig"
          >
            Atualizar
          </NextButton>
        </div>
      </div>
    </div>
  </SectionLayout>
</template>
